import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cpu, Zap, Box, TrendingUp } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { HashDisplay } from '../components/common/HashDisplay';
import { StatCard } from '../components/common/StatCard';
import { getMiners, getNetworkStats } from '../lib/api';
import { formatNumber, formatHashrate } from '../lib/utils';

export default function MinersPage() {
  const [miners, setMiners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [minersRes, statsRes] = await Promise.all([
          getMiners(),
          getNetworkStats(),
        ]);
        setMiners(minersRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching miners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const totalHashrate = miners.reduce((acc, m) => acc + m.hashrate, 0);
  const totalBlocksMined = miners.reduce((acc, m) => acc + m.blocks_mined, 0);
  const totalRewards = miners.reduce((acc, m) => acc + m.total_rewards, 0);

  const columns = [
    {
      header: 'Rank',
      cell: (row, index) => (
        <span className="font-mono font-bold text-muted-foreground">#{index + 1}</span>
      ),
    },
    {
      header: 'Miner',
      cell: (row) => <HashDisplay hash={row.address} type="address" link={`/address/${row.address}`} />,
    },
    {
      header: 'Hashrate',
      cell: (row) => (
        <div>
          <span className="font-mono">{formatHashrate(row.hashrate)}</span>
          <div className="text-xs text-muted-foreground">
            {((row.hashrate / totalHashrate) * 100).toFixed(2)}% of network
          </div>
        </div>
      ),
    },
    {
      header: 'Blocks Mined',
      cell: (row) => <span className="font-mono">{formatNumber(row.blocks_mined, 0)}</span>,
    },
    {
      header: 'Total Rewards',
      cell: (row) => (
        <span className="font-mono text-green-400">
          {formatNumber(row.total_rewards, 4)} GG
        </span>
      ),
    },
    {
      header: 'Last Block',
      cell: (row) => (
        <span 
          className="font-mono text-primary hover:underline cursor-pointer"
          onClick={(e) => { e.stopPropagation(); navigate(`/block/${row.last_block}`); }}
        >
          #{formatNumber(row.last_block, 0)}
        </span>
      ),
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="miners-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-transparent">
            <Cpu className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Miners</h1>
            <p className="text-sm text-muted-foreground">
              PoW miners (70% of block rewards)
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Active Miners"
            value={formatNumber(miners.length, 0)}
            icon={Cpu}
            accentColor="secondary"
          />
          <StatCard
            title="Network Hashrate"
            value={formatHashrate(stats?.hashrate)}
            icon={Zap}
            accentColor="primary"
          />
          <StatCard
            title="Total Blocks Mined"
            value={formatNumber(totalBlocksMined, 0)}
            icon={Box}
            accentColor="success"
          />
          <StatCard
            title="Total Rewards"
            value={`${formatNumber(totalRewards, 0)} GG`}
            icon={TrendingUp}
            accentColor="warning"
          />
        </div>

        {/* Mining Info */}
        <div className="data-card p-6 mb-8">
          <h2 className="text-lg font-bold mb-4">Mining Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Block Time</p>
              <p className="font-mono font-bold">10 Seconds</p>
            </div>
            <div>
              <p className="text-muted-foreground">PoW Share</p>
              <p className="font-mono font-bold text-[#00F0FF]">70%</p>
            </div>
            <div>
              <p className="text-muted-foreground">Halving Interval</p>
              <p className="font-mono font-bold">4 Years</p>
            </div>
            <div>
              <p className="text-muted-foreground">Mining Duration</p>
              <p className="font-mono font-bold">40 Years</p>
            </div>
          </div>
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={miners.map((m, i) => ({ ...m, _index: i }))}
          loading={loading}
          emptyMessage="No miners found"
          onRowClick={(row) => navigate(`/address/${row.address}`)}
        />
      </div>
    </Layout>
  );
}
